import { COLOR, FONT, SIZE } from './tokens';

export function colLetter(n) {
  let letter = '';
  let num = n;
  while (num > 0) {
    const mod = (num - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    num = Math.floor((num - 1) / 26);
  }
  return letter;
}

export function cellRef(row, col) {
  return `${colLetter(col)}${row}`;
}

export function rangeRef(c1, r1, c2, r2) {
  const a = cellRef(r1, c1);
  const b = cellRef(r2, c2);
  return a === b ? a : `${a}:${b}`;
}

export function fill(argb) {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

export function border(color = COLOR.border) {
  const side = { style: 'thin', color: { argb: color } };
  return { top: side, left: side, bottom: side, right: side };
}

export function applyPageSetup(sheet, {
  landscape = false,
  printTitlesRow = null,
  printArea = null,
} = {}) {
  sheet.pageSetup = {
    paperSize: 9, // A4
    orientation: landscape ? 'landscape' : 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    horizontalCentered: true,
    verticalCentered: false,
    margins: { left: 0.4, right: 0.4, top: 0.55, bottom: 0.45, header: 0.3, footer: 0.3 },
  };
  if (printTitlesRow) {
    sheet.pageSetup.printTitlesRow = printTitlesRow;
  }
  if (printArea) {
    sheet.pageSetup.printArea = printArea;
  }
  sheet.headerFooter = {
    oddHeader: '&L&"Segoe UI,Bold"Development Express&CFinancial Reconciliation Report',
    oddFooter: '&LCONFIDENTIAL — Internal Use Only&RPage &P of &N',
  };
}

/** Portrait A4 — columns fit page width; extra rows flow to new pages as data grows. */
export function applyPortraitPrint(sheet, {
  colCount,
  lastRow,
  printTitlesRow = null,
  singlePageThreshold = 55,
  fitOnePage = null,
  landscape = false,
} = {}) {
  const fitHeight = fitOnePage ?? (lastRow <= singlePageThreshold);

  applyPageSetup(sheet, {
    landscape,
    printTitlesRow,
    printArea: rangeRef(1, 1, colCount, lastRow),
  });

  sheet.pageSetup.fitToPage = true;
  sheet.pageSetup.fitToWidth = 1;
  sheet.pageSetup.fitToHeight = fitHeight ? 1 : 0;
  sheet.pageSetup.showGridLines = false;
  sheet.pageSetup.colBreaks = null;
  sheet.pageSetup.rowBreaks = null;

  sheet.rowBreaks = [];

  sheet.views = [{
    state: 'normal',
    showGridLines: false,
    zoomScale: 100,
    activeCell: 'A1',
  }];
}

export function applyFreeze(sheet, { row = 0, col = 0, activeRow = 1, activeCol = 1 } = {}) {
  sheet.views = [{
    state: row || col ? 'frozen' : 'normal',
    ySplit: row,
    xSplit: col,
    zoomScale: 100,
    showGridLines: false,
    activeCell: cellRef(activeRow, activeCol),
  }];
}

export function setWidths(sheet, widths) {
  widths.forEach((w, i) => {
    if (w != null) sheet.getColumn(i + 1).width = w;
  });
}

/** Never shrink below minimum — prevents ₹ amounts showing as ##### */
export function ensureMinColumnWidths(sheet, widths) {
  widths.forEach((minW, i) => {
    if (minW != null) {
      const col = sheet.getColumn(i + 1);
      col.width = Math.max(Number(col.width) || 0, minW);
    }
  });
}

/** Apply explicit widths starting at a column index */
export function setWidthsFrom(sheet, startCol, widths) {
  widths.forEach((w, i) => {
    if (w != null) sheet.getColumn(startCol + i).width = w;
  });
}

/** Hide unused columns so content stays inside the sheet frame */
export function clipSheetToColumns(sheet, colCount) {
  for (let c = colCount + 1; c <= 26; c += 1) {
    const col = sheet.getColumn(c);
    col.width = 0;
    col.hidden = true;
  }
}

/** Two-row premium header — company name + report title, no shrink-to-fit */
export function writeExecutiveHeader(sheet, startRow, companyName, reportTitle, colCount) {
  const rows = [
    { text: companyName, size: 16, bg: COLOR.navyDark, height: 32 },
    { text: reportTitle, size: 12, bg: COLOR.navy, height: 26 },
  ];
  rows.forEach(({ text, size, bg, height }, i) => {
    const row = startRow + i;
    for (let c = 1; c <= colCount; c += 1) {
      const cell = sheet.getCell(row, c);
      cell.fill = fill(bg);
      cell.border = border(COLOR.navy);
    }
    if (colCount > 1) sheet.mergeCells(rangeRef(1, row, colCount, row));
    const cell = sheet.getCell(row, 1);
    cell.value = text;
    cell.font = { name: FONT, size, bold: true, color: { argb: COLOR.white } };
    cell.fill = fill(bg);
    cell.border = border(COLOR.navy);
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: false,
      shrinkToFit: false,
      indent: 0,
    };
    sheet.getRow(row).height = height;
  });
  return startRow + rows.length;
}

/** Only allowed merge: single-row title spanning full width */
export function writeMainTitle(sheet, row, text, colCount, { compact = false } = {}) {
  for (let c = 1; c <= colCount; c += 1) {
    const cell = sheet.getCell(row, c);
    cell.fill = fill(COLOR.navyDark);
    cell.border = border(COLOR.navy);
  }
  const range = rangeRef(1, row, colCount, row);
  if (colCount > 1) sheet.mergeCells(range);
  const cell = sheet.getCell(row, 1);
  cell.value = text;
  cell.font = {
    name: FONT,
    size: compact ? 13 : SIZE.title,
    bold: true,
    color: { argb: COLOR.white },
  };
  cell.fill = fill(COLOR.navyDark);
  cell.border = border(COLOR.navy);
  cell.alignment = {
    vertical: 'middle',
    horizontal: 'center',
    wrapText: false,
    shrinkToFit: false,
    indent: 0,
  };
  sheet.getRow(row).height = compact ? 34 : 36;
}

/** Only allowed merge: section header spanning full width */
export function writeSectionTitle(sheet, row, text, colCount, { center = false } = {}) {
  for (let c = 1; c <= colCount; c += 1) {
    sheet.getCell(row, c).fill = fill(COLOR.goldBg);
    sheet.getCell(row, c).border = border();
  }
  const range = rangeRef(1, row, colCount, row);
  if (colCount > 1) sheet.mergeCells(range);
  const cell = sheet.getCell(row, 1);
  cell.value = text;
  cell.font = { name: FONT, size: SIZE.section, bold: true, color: { argb: COLOR.navy } };
  cell.fill = fill(COLOR.goldBg);
  cell.border = border();
  cell.alignment = {
    vertical: 'middle',
    horizontal: center ? 'center' : 'left',
    indent: center ? 0 : 1,
    wrapText: false,
    shrinkToFit: false,
  };
  sheet.getRow(row).height = 28;
}

/** Navy header row styling for Excel tables */
export function styleTableHeader(sheet, headerRow, specs) {
  specs.forEach(({ col, align }) => {
    const cell = sheet.getCell(headerRow, col);
    cell.alignment = { horizontal: align, vertical: 'middle', indent: align === 'left' ? 1 : 0 };
    cell.font = { name: FONT, size: SIZE.tableHead, bold: true, color: { argb: COLOR.white } };
  });
}

function writeMergedMetaCell(sheet, row, startCol, endCol, text) {
  if (endCol > startCol) {
    sheet.mergeCells(rangeRef(startCol, row, endCol, row));
  }
  const cell = sheet.getCell(row, startCol);
  cell.value = text;
  cell.font = { name: FONT, size: SIZE.meta, color: { argb: COLOR.muted } };
  cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: false, indent: 1 };
}

/**
 * Premium two-row meta band — zebra fill, borders, aligned merges.
 */
export function writePremiumMetaBand(sheet, startRow, maxCol, topLine, bottomLine) {
  for (let r = startRow; r <= startRow + 1; r += 1) {
    for (let c = 1; c <= maxCol; c += 1) {
      sheet.getCell(r, c).fill = fill(COLOR.zebra);
    }
  }
  const writeCell = (row, from, to, text) => {
    const end = Math.min(to, maxCol);
    if (end > from) sheet.mergeCells(rangeRef(from, row, end, row));
    const cell = sheet.getCell(row, from);
    cell.value = text;
    cell.font = { name: FONT, size: SIZE.meta, color: { argb: COLOR.navy } };
    cell.fill = fill(COLOR.zebra);
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: false };
    cell.border = border();
  };
  topLine.forEach(({ text, from, to }) => writeCell(startRow, from, to, text));
  bottomLine.forEach(({ text, from, to }) => writeCell(startRow + 1, from, to, text));
  sheet.getRow(startRow).height = 24;
  sheet.getRow(startRow + 1).height = 24;
}

function metaColumnSlots(count, maxCol) {
  if (count <= 0) return [];
  if (maxCol >= 13) {
    const wide = [1, 4, 8, 12, 16];
    return wide.filter((c) => c <= maxCol).slice(0, count);
  }
  if (count === 1) return [1];
  return Array.from({ length: count }, (_, i) => (
    Math.round(1 + (i * (maxCol - 1)) / (count - 1))
  ));
}

export function writeMetaLine(sheet, row, parts, maxCol = 16) {
  const filtered = parts.filter(Boolean);
  const slots = metaColumnSlots(filtered.length, maxCol);
  const wideMetaWidths = { 1: 36, 4: 28, 5: 28, 8: 28, 9: 28, 12: 28, 13: 28, 16: 22 };

  filtered.forEach((text, i) => {
    const col = slots[i];
    const cell = sheet.getCell(row, col);
    cell.value = text;
    const minWidth = maxCol >= 13
      ? (wideMetaWidths[col] || 20)
      : 14;
    sheet.getColumn(col).width = Math.max(sheet.getColumn(col).width || 0, minWidth);
    cell.font = { name: FONT, size: SIZE.meta, color: { argb: COLOR.muted } };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });
  sheet.getRow(row).height = 22;
}

export function writeSpacer(sheet, row, height = 14) {
  sheet.getRow(row).height = height;
}

/** Visible white-space band between dashboard sections */
export function writeSectionGap(sheet, row, colCount, { height = 18 } = {}) {
  for (let c = 1; c <= colCount; c += 1) {
    sheet.getCell(row, c).value = null;
    sheet.getCell(row, c).fill = fill(COLOR.white);
  }
  sheet.getRow(row).height = height;
}

/** Estimate row height for wrapped text in a column width (Excel char units) */
export function estimateWrappedRowHeight(text, colWidth, {
  minHeight = 28,
  lineHeight = 16,
  padding = 12,
} = {}) {
  const str = String(text || '').trim();
  if (!str) return minHeight;

  const charsPerLine = Math.max(8, Math.floor(colWidth * 0.72));
  const words = str.split(/\s+/).filter(Boolean);
  let lines = 1;
  let currentLen = 0;

  words.forEach((word) => {
    const chunk = currentLen === 0 ? word.length : word.length + 1;
    if (chunk > charsPerLine) {
      lines += Math.ceil(word.length / charsPerLine);
      currentLen = 0;
      return;
    }
    if (currentLen + chunk > charsPerLine) {
      lines += 1;
      currentLen = word.length;
    } else {
      currentLen += chunk;
    }
  });

  return Math.max(minHeight, lines * lineHeight + padding);
}

export function formatGeneratedAt() {
  return new Date().toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatPeriod(from, to) {
  if (from && to) return `${from} to ${to}`;
  if (from) return `From ${from}`;
  if (to) return `Until ${to}`;
  return 'All dates';
}

export function buildSparkBar(value, max, width = 12) {
  if (!max || !value) return '';
  const filled = Math.max(1, Math.round((value / max) * width));
  return '▮'.repeat(Math.min(filled, width));
}

export function uniqueTableName(base, used) {
  let name = base.replace(/[^A-Za-z0-9_]/g, '_');
  if (!/^[A-Za-z]/.test(name)) name = `T_${name}`;
  let candidate = name;
  let i = 2;
  while (used.has(candidate)) {
    candidate = `${name}_${i}`;
    i += 1;
  }
  used.add(candidate);
  return candidate;
}
